// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import WindowScroll from '@ta/events.window-scroll';

/** Computes the proper positioning for a fixed position element to handle iOS Safari */

type ExternalProps = {|
  /** The pixel offset from the bottom-side of the viewport */
  bottom: number,
  /** The pixel offset from the right-side of the viewport */
  right: number,
  /** A render prop that exposes the correctly computed positions */
  children: ({ bottom?: number, right?: number, top?: number, left?: number }) => React.Node,
|};

type Props = {|
  ...ExternalProps,
  /** Whether the user agent is from iOS Safari MW */
  isIOSSafari: boolean,
  /** Whether the current locale is RTL */
  isRTL: boolean,
|};

type State = {|
  bottomOffset: number,
|};

// A hack to calculate the zoom level for a mobile device. Used so the position hack works regardless of zoom level.
// Don't test window styles
/* istanbul ignore next */
const zoomLevel = () => {
  /* eslint-disable no-restricted-globals */
  const clientWidth = document?.documentElement?.clientWidth;
  const innerWidth = window?.innerWidth;
  if (clientWidth && innerWidth) {
    return clientWidth / innerWidth;
  }
  /* eslint-enable no-restricted-globals */
  return 1;
};

class Positioner extends React.PureComponent<Props, State> {
  state = { bottomOffset: 0 };

  // Enable buggie mode bottom calculation fix, which we only do in non-webview iOS Safari.
  reposition =
    // Don't test positions
    /* istanbul ignore next */
    () => {
      /* eslint-disable no-restricted-globals */
      if (
        document &&
        document.documentElement &&
        window &&
        window.innerHeight > window.innerWidth
      ) {
        /* On certain iOS versions, the client height does not match the inner height exactly because of the presence
        of nav/menu bars. Bottom positioning is relative to the documentElement, but the documentElement is not necessarily
        the viewport. This bug fix does not work particularly well in landscape mode, so scope it to Portrait mode only
       */
        const difference =
          document.documentElement.clientHeight - (zoomLevel() || 1) * window.innerHeight;
        const offset = difference > 0 ? difference : 0;
        this.setState({ bottomOffset: offset });
      }
      /* eslint-enable no-restricted-globals */
    };

  render() {
    const { bottom, right, isRTL } = this.props;
    const computedBottom = bottom + this.state.bottomOffset;
    const position = { bottom: computedBottom, [isRTL ? 'left' : 'right']: right };
    return (
      <>
        {this.props.children(position)}
        {this.props.isIOSSafari && <WindowScroll callback={this.reposition} throttle={500} />}
      </>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  isIOSSafari:
    state.meta.device.os.family === 'IOS' &&
    state.meta.device.browser.family === 'SAFARI' &&
    !state.meta.isNativeWebview,
  isRTL: state.i18n.isRtl,
}))(Positioner);
